'use strict';
import toast from '@components/Common/Toast.js';
import { CheckIcon, CircleCrossIcon, WarningIcon } from '@icons/index.js';
import { UIButton, UIDiv, UIText } from '@ui';

class AiAnalyzerConfirmation extends UIDiv {
  constructor(onConfirm, onReject) {
    super();
    this.setId('ai-analyzer-confirmation');
    this.addClass('hidden');
    this.onConfirm = onConfirm;
    this.onReject = onReject;
    this.currentToken = null;

    this.#buildComponent();
  }

  #buildComponent() {
    // Main confirmation container
    const confirmationContainer = new UIDiv();
    confirmationContainer.addClass(
      'bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 space-y-3',
    );

    // Header with warning icon and title
    const headerDiv = new UIDiv();
    headerDiv.addClass('flex items-center gap-2 mb-2');

    const warningIcon = new WarningIcon({
      size: 16,
      color: 'none',
    });

    const titleText = new UIText('Confirmation Required');
    titleText.addClass('font-semibold text-yellow-800');

    headerDiv.add(warningIcon);
    headerDiv.add(titleText);
    confirmationContainer.add(headerDiv);

    // Message container
    this.messageContainer = new UIDiv();
    this.messageContainer.addClass('text-yellow-700 mb-3');
    confirmationContainer.add(this.messageContainer);

    // Tokens list container
    this.tokensContainer = new UIDiv();
    this.tokensContainer.addClass('space-y-2 mb-4');
    confirmationContainer.add(this.tokensContainer);

    // Action buttons
    const actionsDiv = new UIDiv();
    actionsDiv.addClass('flex gap-3 justify-end');

    // Reject button
    const rejectButton = new UIButton();
    rejectButton.addClass(
      'px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 border border-red-300 rounded-md flex items-center gap-2 transition-colors',
    );
    const rejectIcon = new CircleCrossIcon({ size: 16, color: 'none' });
    const rejectText = new UIText('Reject');
    rejectButton.add(rejectIcon);
    rejectButton.add(rejectText);
    rejectButton.onClick(() => this.#handleReject());

    // Confirm button
    const confirmButton = new UIButton();
    confirmButton.addClass(
      'px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 border border-green-300 rounded-md flex items-center gap-2 transition-colors',
    );
    const confirmIcon = new CheckIcon({ size: 16, color: 'none' });
    const confirmText = new UIText('Confirm');
    confirmButton.add(confirmIcon);
    confirmButton.add(confirmText);
    confirmButton.onClick(() => this.#handleConfirm());

    actionsDiv.add(rejectButton);
    actionsDiv.add(confirmButton);
    confirmationContainer.add(actionsDiv);

    this.add(confirmationContainer);
  }

  showConfirmation(confirmToken) {
    if (!confirmToken) return;

    this.currentToken = confirmToken;

    // Show the confirmation UI
    this.removeClass('hidden');

    // Scroll to the confirmation
    this.dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  hideConfirmation() {
    this.addClass('hidden');
    this.currentToken = null;
  }

  #handleConfirm() {
    if (!this.currentToken) {
      toast.error('No confirmation token available');
      return;
    }

    // Call the confirmation API with tokens
    this.onConfirm(this.currentToken);
    this.hideConfirmation();
  }

  #handleReject() {
    this.onReject();
    this.hideConfirmation();
  }
}

export { AiAnalyzerConfirmation };
